import type { ApiRootRouteLabel } from "griptonite-api-client";

import { DragIndicator, MoreHoriz } from "@mui/icons-material";
import {
  Box,
  Dropdown,
  IconButton,
  ListDivider,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import { useRef, useState } from "react";

import { useDeleteRouteLabelMutation, useUpdateRouteLabelMutation } from "@/hooks/routeLabels";

import InlineNameField from "./InlineNameField";
import { dropIndicatorSx, ROWS_SCOPE, useSortableItem } from "./sortable";
import VisibleToClimbersIcon from "./VisibleToClimbersIcon";

// A label with no options: a flag a route either has or doesn't. It takes a
// whole row, with nothing to expand.
export default function SingleLabelRow({ label }: { label: ApiRootRouteLabel }) {
  const updateLabel = useUpdateRouteLabelMutation();
  const deleteLabel = useDeleteRouteLabelMutation();
  const [isRenaming, setIsRenaming] = useState(false);

  const gripRef = useRef<HTMLDivElement>(null);
  const { ref, isDragging, edge } = useSortableItem<HTMLDivElement>({
    scope: ROWS_SCOPE,
    id: label.uuid,
    axis: "vertical",
    handleRef: gripRef,
    previewShape: "row",
  });

  return (
    <Box
      ref={ref}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        "&:not(:first-of-type)": { borderTop: "1px solid", borderColor: "divider" },
        ...dropIndicatorSx(edge, "vertical"),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{
          minHeight: 52,
          pl: 1.25,
          pr: 1.5,
          "&:hover": { backgroundColor: "background.level1" },
        }}
      >
        {/* Lines the grip up with the rows that have a chevron. */}
        <Box
          ref={gripRef}
          aria-hidden
          sx={{ display: "flex", color: "neutral.500", cursor: "grab" }}
        >
          <DragIndicator fontSize="small" />
        </Box>
        <Box aria-hidden sx={{ width: 32, flex: "none" }} />

        {isRenaming ? (
          <Box sx={{ flex: 1 }}>
            <InlineNameField
              label={`Rename ${label.name}`}
              initialValue={label.name}
              width={260}
              onSave={(name) => updateLabel.mutateAsync({ uuid: label.uuid, name })}
              onClose={() => setIsRenaming(false)}
            />
          </Box>
        ) : (
          <>
            <Typography level="title-md" noWrap sx={{ flex: "0 0 150px" }}>
              {label.name}
            </Typography>
            <Box sx={{ flex: 1 }} />
          </>
        )}

        <Stack direction="row" alignItems="center" gap={0.5}>
          {label.isPublic ? <VisibleToClimbersIcon /> : null}
          <Dropdown>
            <MenuButton
              slots={{ root: IconButton }}
              slotProps={{
                root: {
                  size: "sm",
                  variant: "plain",
                  color: "neutral",
                  "aria-label": `Options for ${label.name}`,
                },
              }}
            >
              <MoreHoriz fontSize="small" />
            </MenuButton>
            <Menu size="sm" placement="bottom-end" sx={{ minWidth: 220 }}>
              <MenuItem onClick={() => setIsRenaming(true)}>Rename</MenuItem>
              <MenuItem
                role="menuitemcheckbox"
                aria-checked={label.isPublic}
                onClick={() => updateLabel.mutate({ uuid: label.uuid, isPublic: !label.isPublic })}
                sx={{ justifyContent: "space-between", gap: 2 }}
              >
                Visible to climbers
                <Switch
                  size="sm"
                  checked={label.isPublic}
                  // The menu item does the toggling.
                  onChange={() => undefined}
                  slotProps={{ input: { tabIndex: -1 } }}
                />
              </MenuItem>
              <ListDivider />
              {/* Labels on routes can only be archived, so those routes keep them. */}
              {label.routeCount === 0 ? (
                <MenuItem color="danger" onClick={() => deleteLabel.mutate(label.uuid)}>
                  Delete
                </MenuItem>
              ) : (
                <MenuItem
                  color="danger"
                  onClick={() => updateLabel.mutate({ uuid: label.uuid, isActive: false })}
                >
                  Archive
                </MenuItem>
              )}
            </Menu>
          </Dropdown>
        </Stack>
      </Stack>
    </Box>
  );
}
